import React from 'react'
import { Button, Form, FormTextInput, ImageModal } from '@superhi/design'
import { useMutation } from '@apollo/client'
import { string } from 'yup'
import { setErrorsFromApollo } from '@superhi/utils'

import { API } from '../../../../../../api'

import { LEAD_CREATE } from './mutations'
import { EMAIL } from './config'

type FormValues = {
  email: string
}

type Props = Omit<
  React.ComponentProps<typeof ImageModal>,
  'portalElementId' | 'title' | 'subtitle' | 'imageFit' | 'imageBackgroundColor'
> & {
  title: string
  subtitle?: string
  downloadUrl: string
  imageBackgroundColor?: React.ComponentProps<typeof ImageModal>['imageBackgroundColor']

  leadTag: string
  onEmailEntered?: (email: string) => void
  onDownloadClicked?: (email: string) => void
}

const EmailModal: React.FC<Props> = ({
  title,
  subtitle = 'After entering your email address below, you’ll be able to start downloading straight away!',
  downloadUrl,
  image,
  onClose,
  imageBackgroundColor = 'YELLOW_20',

  leadTag,

  onEmailEntered,
  onDownloadClicked,
}) => {
  const [leadCreate, leadCreateMutation] = useMutation<
    API.GuideLeadCreate,
    API.GuideLeadCreateVariables
  >(LEAD_CREATE)

  const handleClickDownload = React.useCallback(() => {
    if (onDownloadClicked && leadCreateMutation.data?.leadCreate?.email) {
      onDownloadClicked(leadCreateMutation.data?.leadCreate?.email)
    }
  }, [leadCreateMutation.data, onDownloadClicked])

  return (
    <ImageModal
      onClose={onClose}
      title={title}
      subtitle={!leadCreateMutation.data ? subtitle : 'Thanks!'}
      image={image}
      imageBackgroundColor={imageBackgroundColor}
      imageFit="contain"
    >
      {!leadCreateMutation.data ? (
        <Form<FormValues>
          name="email-modal"
          initialValues={{ email: '' }}
          validationSchema={{ email: string().required() }}
          onSubmit={async (values, helpers) => {
            try {
              await leadCreate({
                variables: {
                  email: values.email,
                  tag: leadTag,
                },
              })

              if (onEmailEntered) {
                onEmailEntered(values.email)
              }
            } catch (e) {
              setErrorsFromApollo(helpers.setFieldError, e)
            }
          }}
        >
          <Form.Row>
            <FormTextInput {...EMAIL} />
          </Form.Row>
        </Form>
      ) : (
        <Button href={downloadUrl} onClick={handleClickDownload} isExternal>
          Click here to download
        </Button>
      )}
    </ImageModal>
  )
}

export default EmailModal
